import { useEffect, useState } from 'react';
import type { UpdateStripeSettingsInput } from '@klaro7/validators';
import { Badge, Button, Input, Spinner } from '@klaro7/ui';

import { usePinGate } from '@/components/PinGate';
import {
  useStripeSettings,
  useUpdateStripeSettings,
  useTestStripe,
} from '../hooks/useServicesSettings';

/** A masked secret is the server-returned placeholder (e.g. "****abcd"). */
function isMasked(value: string): boolean {
  return value.startsWith('****');
}

function extractErrorDetail(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = err as {
      response?: {
        data?: { error?: { message?: string }; message?: string };
        status?: number;
        statusText?: string;
      };
    };
    const apiMsg = resp.response?.data?.error?.message || resp.response?.data?.message;
    const status = resp.response?.status;
    if (apiMsg) return `[${status}] ${apiMsg}`;
    if (status) return `HTTP ${status}: ${resp.response?.statusText || 'Errore sconosciuto'}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Stripe API credentials configuration. Super-admin only — the parent gates
 * rendering on the current user's role.
 *
 * Secret fields (secretKey, webhookSecret) arrive masked from the server. We
 * load the masked value into the input and only include a field in the PUT
 * payload when the admin actually types a new value (i.e. it no longer equals
 * the original masked string), so we never overwrite a stored secret with its
 * mask.
 */
export function StripeSettingsSection() {
  const { requirePin, pinDialog } = usePinGate();
  const { data: settings, isLoading } = useStripeSettings(true);
  const updateMutation = useUpdateStripeSettings();
  const testMutation = useTestStripe();

  const [publishableKey, setPublishableKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [enabled, setEnabled] = useState(false);

  // Original masked secret values (to detect whether the admin typed a new one).
  const [savedSecretKey, setSavedSecretKey] = useState('');
  const [savedWebhookSecret, setSavedWebhookSecret] = useState('');

  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; accountId?: string; message?: string } | null>(null);

  useEffect(() => {
    if (settings) {
      setPublishableKey(settings.publishableKey ?? '');
      setSecretKey(settings.secretKey ?? '');
      setWebhookSecret(settings.webhookSecret ?? '');
      setEnabled(settings.enabled ?? false);
      setSavedSecretKey(settings.secretKey ?? '');
      setSavedWebhookSecret(settings.webhookSecret ?? '');
    }
  }, [settings]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.type === 'error' ? 15000 : 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // True once a non-empty, non-masked secret has been stored on the server.
  const hasSavedSecretKey = savedSecretKey.length > 0 && isMasked(savedSecretKey);

  async function handleSave() {
    const payload: UpdateStripeSettingsInput = {
      publishableKey,
      enabled,
    };
    // Only send secrets the admin actually changed (typed a new value).
    if (secretKey !== savedSecretKey) {
      payload.secretKey = secretKey;
    }
    if (webhookSecret !== savedWebhookSecret) {
      payload.webhookSecret = webhookSecret;
    }

    try {
      await updateMutation.mutateAsync(payload);
      setToast({ type: 'success', message: 'Configurazione Stripe salvata con successo' });
    } catch (err) {
      setToast({ type: 'error', message: `Errore nel salvataggio Stripe: ${extractErrorDetail(err)}` });
    }
  }

  async function handleTest() {
    setTestResult(null);
    // If the admin typed a new (non-masked) secret key, test that one;
    // otherwise the server falls back to the stored decrypted key.
    const typedNewKey = secretKey !== savedSecretKey && !isMasked(secretKey) && secretKey.length > 0;
    try {
      const result = await testMutation.mutateAsync(typedNewKey ? { secretKey } : {});
      setTestResult({ ok: result.ok, accountId: result.accountId, message: result.message });
    } catch (err) {
      setTestResult({ ok: false, message: extractErrorDetail(err) });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">Stripe</h3>
            <p className="text-xs text-neutral-600">Credenziali API Stripe per i pagamenti (solo super admin)</p>
          </div>
        </div>
        <Badge variant={enabled ? 'success' : 'default'}>
          {enabled ? 'Attivo' : 'Disattivato'}
        </Badge>
      </div>

      <div className="space-y-4 p-5">
        {toast && (
          <div
            className={`rounded-lg border px-4 py-2.5 text-sm font-medium ${
              toast.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {toast.message}
          </div>
        )}

        <Input
          label="Publishable Key"
          value={publishableKey}
          onChange={(e) => setPublishableKey(e.target.value)}
          placeholder="pk_test_..."
          helperText="Chiave pubblica, salvata in chiaro."
        />

        <Input
          label="Secret Key"
          type="password"
          value={secretKey}
          onChange={(e) => setSecretKey(e.target.value)}
          placeholder="sk_test_..."
          helperText="Chiave segreta, cifrata. Lascia invariato il valore mascherato per non sovrascriverla."
        />

        <Input
          label="Webhook Secret"
          type="password"
          value={webhookSecret}
          onChange={(e) => setWebhookSecret(e.target.value)}
          placeholder="whsec_..."
          helperText="Segreto webhook, cifrato. Lascia invariato il valore mascherato per non sovrascriverlo."
        />

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
          />
          Abilitato
        </label>

        <div className="flex items-center justify-between border-t border-neutral-100 pt-4">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="md"
              onClick={handleTest}
              loading={testMutation.isPending}
              disabled={!hasSavedSecretKey && (isMasked(secretKey) || secretKey.length === 0)}
              title={
                !hasSavedSecretKey && secretKey.length === 0
                  ? 'Inserisci o salva prima la Secret Key'
                  : 'Verifica la connessione a Stripe'
              }
            >
              Testa
            </Button>
            {testResult && (
              <div
                className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ${
                  testResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                }`}
              >
                {testResult.ok ? (
                  <>
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>OK — Account {testResult.accountId ?? 'collegato'}</span>
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>KO — {testResult.message ?? 'Errore'}</span>
                  </>
                )}
              </div>
            )}
          </div>
          {pinDialog}
          <Button onClick={() => requirePin(handleSave)} loading={updateMutation.isPending}>
            Salva Stripe
          </Button>
        </div>
      </div>
    </div>
  );
}
